import { useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { V2Destination } from '../../utils/v2Routes';
import OpenInV2Link from './OpenInV2Link';

const SECTIONS = [
  {
    title: 'See the bigger picture',
    items: [
      'New account dashboard with a performance overview',
      'Campaign Performance dashboard: status, pacing and key metrics in one view',
      'Campaign view for delivery and engagement',
      'Custom reporting for deeper analysis',
    ],
  },
  {
    title: 'More control over your campaigns',
    items: [
      'Always-on campaigns with the "No specific target" goal',
      'Daily, weekly or monthly limits',
      'Marketplace: clearer earnings overview and improved approval flow',
    ],
  },
  {
    title: 'More ways to tell your story',
    items: [
      'Resize images directly in the editor',
      'Embed videos in your content',
      'Video engagement reporting',
    ],
  },
  {
    title: 'Optimise for meaningful outcomes',
    items: [
      'Measure and optimise towards leads and purchases',
      'Track custom events in advertorials and on landing pages',
      'Optimise towards long reads',
    ],
  },
];

interface Props {
  id: string;
  open: boolean;
  onClose: () => void;
  destination: V2Destination;
}

export default function WhatsChangingDrawer({ id, open, onClose, destination }: Props) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog id={id} open={open} onClose={onClose} initialFocus={closeButtonRef} className="relative z-50">
      <div className="fixed inset-0 bg-[rgba(17,24,39,0.35)]" aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 flex max-w-full">
        <Dialog.Panel className="flex h-full w-[460px] max-w-full flex-col bg-white shadow-[-8px_0_24px_rgba(0,0,0,0.12)]">
          <div className="flex items-start gap-4 px-6 pb-4 pt-6">
            <div className="flex-1">
              <span className="inline-block rounded-full bg-[#EEF2FF] px-2 py-0.5 text-xs font-semibold text-[#4F46E5]">
                Open beta
              </span>
              <Dialog.Title className="mt-2 text-xl font-semibold text-gray-900">
                What's changing in V2
              </Dialog.Title>
              <p className="mt-1 text-sm text-[#6B7280]">
                A new interface and more ways to understand, manage and optimise your campaigns. V1 stays
                available during the beta.
              </p>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="-mr-2 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6 pt-2">
            {SECTIONS.map((section, index) => (
              <section key={section.title} className={index > 0 ? 'border-t border-[#F0F1F3] pt-4 mt-4' : ''}>
                <h3 className="text-[15px] font-semibold text-gray-900">{section.title}</h3>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-[1.45] text-[#4B5563]">
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            ))}

            <div className="mt-6 rounded-lg bg-[#F9FAFB] px-4 py-[14px]">
              <h3 className="text-sm font-semibold text-gray-900">Still in beta, still improving</h3>
              <p className="mt-1 text-sm leading-[1.45] text-[#4B5563]">
                More features are rolling out. V2 is currently slower than it should be — speed and platform
                performance are a priority during the beta.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-[#E5E7EB] px-6 py-4">
            <p className="text-[13px] text-[#6B7280]">Opens {destination.label} in V2</p>
            <OpenInV2Link destination={destination} source="v2_drawer" />
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
